import React, { useState, useEffect, useRef } from 'react';
import { ChevronUp, Plus, Check } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { useFavoriteColors } from '@/hooks/useFavoriteColors.tsx';

interface ColorInputProps {
  value: string;
  onChange: (hex: string) => void;
  compact?: boolean;
  /** Alpha value 0-1. When provided, an opacity slider is shown. */
  alpha?: number;
  /** Called when the alpha slider changes. */
  onAlphaChange?: (alpha: number) => void;
}

export function ColorInput({
  value,
  onChange,
  compact = false,
  alpha,
  onAlphaChange,
}: ColorInputProps) {
  const showAlpha = alpha !== undefined && onAlphaChange !== undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => value.replace('#', ''));
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(
    undefined,
  );

  const { favorites, addFavorite, removeFavorite, isFavorite } =
    useFavoriteColors();

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value.toUpperCase();
    // Allow only hex characters
    if (/^[A-F0-9]*$/.test(newVal) && newVal.length <= 6) {
      setInputValue(newVal);
      // Only update the actual color when we have a valid 6-character hex
      if (newVal.length === 6) {
        onChange(`#${newVal}`);
      }
    }
  };

  const handleHexBlur = () => {
    // On blur, if the value is invalid, reset to the current color
    if (inputValue.length !== 6) {
      setInputValue(value.replace('#', ''));
    }
  };

  // Update input value when color prop changes
  useEffect(() => {
    setInputValue(value.replace('#', ''));
  }, [value]);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
  }, [isOpen]);

  const heightClass = compact ? 'h-8' : 'h-[48px]';
  const alreadySaved = isFavorite(value);
  const canAdd = favorites.length < 16 && !alreadySaved;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label="Toggle color picker"
          ref={triggerRef}
          className={`relative flex ${heightClass} w-full items-center justify-between overflow-hidden rounded-md bg-muted pl-3 pr-0 text-sm transition-colors duration-200 ease-out focus:outline-none [@media(hover:hover)]:hover:bg-muted/80`}
        >
          <div className="relative z-20 flex items-center gap-3">
            <div
              className={`rounded-full shadow-sm ${compact ? 'h-4 w-4' : 'h-6 w-6'}`}
              style={{
                backgroundColor: value,
                opacity: showAlpha ? alpha : 1,
              }}
            />
            <div className="flex items-center gap-2 font-mono text-sm uppercase text-foreground">
              <span className="text-muted-foreground">#</span>
              <input
                type="text"
                value={inputValue}
                onChange={handleHexChange}
                onBlur={handleHexBlur}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onFocus={(e) => {
                  e.stopPropagation();
                  e.currentTarget.select();
                }}
                className="cursor-text rounded-md bg-transparent px-2 py-0.5 leading-none text-foreground outline-none transition-colors duration-200 ease-out selection:bg-sky-500/30 selection:text-foreground focus:bg-background [@media(hover:hover)]:hover:bg-background/50"
                style={{
                  width: `calc(${Math.max(1, inputValue.length)}ch + 1rem)`,
                }}
                spellCheck="false"
              />
              {showAlpha && (
                <span className="text-xs text-muted-foreground">
                  {Math.round(alpha * 100)}%
                </span>
              )}
            </div>
          </div>
          <div
            className={`relative z-20 flex ${compact ? 'h-8 w-8' : 'h-12 w-12'} items-center justify-center`}
          >
            <ChevronUp
              className={`h-5 w-5 text-foreground transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto rounded-md border-border bg-background p-4 shadow-md"
        style={{ width: popoverWidth ?? 'auto', minWidth: '200px' }}
      >
        <div className="flex flex-col gap-3">
          <HexColorPicker
            color={value}
            onChange={(newColor) => onChange(newColor.toUpperCase())}
            style={{ height: '120px', width: '100%' }}
          />
          {showAlpha && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Opacity</span>
                <span className="text-xs tabular-nums text-foreground">
                  {Math.round(alpha * 100)}%
                </span>
              </div>
              <Slider
                value={[alpha]}
                onValueChange={([v]) => onAlphaChange(v)}
                min={0}
                max={1}
                step={0.01}
                className="h-6"
              />
            </div>
          )}

          {/* Favorites section */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Favorites</span>
              <button
                aria-label={
                  alreadySaved ? 'Already in favorites' : 'Add to favorites'
                }
                onClick={() => addFavorite(value)}
                disabled={!canAdd}
                className={`flex h-5 w-5 items-center justify-center rounded transition-colors duration-150 focus:outline-none ${
                  alreadySaved
                    ? 'cursor-default text-muted-foreground/40'
                    : canAdd
                      ? 'text-muted-foreground [@media(hover:hover)]:hover:bg-muted [@media(hover:hover)]:hover:text-foreground'
                      : 'cursor-not-allowed text-muted-foreground/40'
                }`}
              >
                {alreadySaved ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
              </button>
            </div>

            {favorites.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {favorites.map((fav) => (
                  <ContextMenu key={fav}>
                    <ContextMenuTrigger asChild>
                      <button
                        aria-label={`Select color ${fav}`}
                        onClick={() => onChange(fav)}
                        className={`h-6 w-6 flex-shrink-0 rounded-full shadow-sm ring-offset-background transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 [@media(hover:hover)]:hover:scale-110 ${fav === value.toUpperCase() ? 'ring-2 ring-ring ring-offset-1' : ''}`}
                        style={{ backgroundColor: fav }}
                      />
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      <ContextMenuItem
                        onSelect={() => removeFavorite(fav)}
                        className="text-destructive focus:text-destructive"
                      >
                        Remove from favorites
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground/60">
                No favorites yet. Pick a color and press +.
              </p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
